import { lazy, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { ClientSuspense } from 'dibs-elements/src/ClientSuspense';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';

import { type PdpRespFeaturedItemsGridLazy_viewer$key } from './__generated__/PdpRespFeaturedItemsGridLazy_viewer.graphql';
import { type PdpRespFeaturedItemsGridLazy_user$key } from './__generated__/PdpRespFeaturedItemsGridLazy_user.graphql';
import { type PdpRespFeaturedItemsGridLazy_item$key } from './__generated__/PdpRespFeaturedItemsGridLazy_item.graphql';

const PdpRespFeaturedItemsGrid = lazy(
    () => import(/* webpackChunkName: "PdpRespFeaturedItemsGrid'" */ './PdpRespFeaturedItemsGrid')
);

type Props = {
    viewer: PdpRespFeaturedItemsGridLazy_viewer$key;
    user: PdpRespFeaturedItemsGridLazy_user$key | null | undefined;
    item: PdpRespFeaturedItemsGridLazy_item$key;
    currency: string;
    afterDisplay?: boolean | null;
    isUnavailablePdp?: boolean;
};

const viewerFragment = graphql`
    fragment PdpRespFeaturedItemsGridLazy_viewer on Viewer {
        ...PdpRespFeaturedItemsGrid_viewer
        sponsoredItems(
            userId: $userId
            guestId: $guestId
            itemId: $itemId
            isTrade: $isTrade
            priceBookName: $priceBookName
            userCountryCode: $userCountryCode
            pageSize: $numOfSponsoredItems
            maxItems: $maxSponsoredItems
            minItemsSteps: $minSponsoredItemsSteps
        ) {
            ...PdpRespFeaturedItemsGrid_sponsoredItems
        }
    }
`;

const userFragment = graphql`
    fragment PdpRespFeaturedItemsGridLazy_user on User {
        ...PdpRespFeaturedItemsGrid_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespFeaturedItemsGridLazy_item on Item {
        ...PdpRespFeaturedItemsGrid_item
    }
`;

const PdpRespFeaturedItemsGridLazyComponent: FC<Props> = ({
    viewer: viewerRef,
    user: userRef,
    item: itemRef,
    currency,
    afterDisplay,
    isUnavailablePdp,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);
    const sponsoredItems = viewer?.sponsoredItems;

    return (
        <PdpRespFeaturedItemsGrid
            viewer={viewer}
            sponsoredItems={sponsoredItems}
            user={user}
            item={item}
            currency={currency}
            afterDisplay={afterDisplay}
            isUnavailablePdp={isUnavailablePdp}
        />
    );
};

export const PdpRespFeaturedItemsGridLazy: FC<Props> = ({
    viewer,
    user,
    item,
    currency,
    afterDisplay,
    isUnavailablePdp,
}) => {
    const { isBot, isPdpBottomNewSlModuleTestVariant } = useServerVarsContext();

    if (isBot || !afterDisplay || !isPdpBottomNewSlModuleTestVariant) {
        return null;
    }

    return (
        <ClientSuspense fallback={null}>
            <PdpRespFeaturedItemsGridLazyComponent
                viewer={viewer}
                user={user}
                item={item}
                currency={currency}
                afterDisplay={afterDisplay}
                isUnavailablePdp={isUnavailablePdp}
            />
        </ClientSuspense>
    );
};
